import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../../Shared/helpers';
import { Modal } from '../../../Shared/Modal/Modal';
import { Pagination } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Pager } from '../../../Shared/UI/Pager';
import { SearchFilterInput } from '../../../Shared/UI/SearchFilterInput';
import { Product, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { SettingsLayout } from '../SettingsLayout';
import AddProductForm from './AddProductForm';
import ProductCard from './ProductCard';

export interface ProductsPageProps extends SharedProps {
  products: Pagination<Product>;
}

export default function ProductsPage({
  products,
  charity,
  params,
}: ProductsPageProps) {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(!!params.add_product);

  const containerSize = params.search || products.meta.total > 0 ? 'lg' : 'md';
  const showSearch = params.search || products.meta.total > 0;

  return (
    <>
      <Header>
        <Container size={containerSize}>
          <div className="flex items-center justify-between">
            <HeaderTitle>
              {t('backend:products')}
            </HeaderTitle>
            <Button onClick={() => setShowModal(true)}>
              {t('backend:add_product')}
            </Button>
          </div>
        </Container>
      </Header>

      <Container size={containerSize} className="space-y-6">
        {showSearch && (
          <div className="w-96">
            <SearchFilterInput id="search" />
          </div>
        )}

        {products.data.length > 0 && (
          <div className="space-y-2">
            {products.data.map((product) => (
              <ProductCard
                product={product}
                charity={charity}
                key={product.id}
              />
            ))}
          </div>
        )}

        {products.meta.last_page > 1 && (
          <Pager data={products} />
        )}

        {!!params.search && products.meta.total === 0 && (
          <Card className="text-center text-slate-500 py-24">
            <img
              src={assetUrl('images/no-results.svg')}
              alt={t('backend:no_results')}
              width={120}
              className="block mx-auto mb-6"
            />
            {t('backend:no_results')}
          </Card>
        )}

        {!params.search && products.meta.total === 0 && (
          <Card className="text-center whitespace-pre-line text-slate-500">
            <h2>
              {t('backend:no_products_yet')}
            </h2>
            {t('backend:no_products_yet_description')}
          </Card>
        )}
      </Container>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="sm">
        <AddProductForm />
      </Modal>
    </>
  );
}

ProductsPage.layout = (page: ReactNode) => <Layout><SettingsLayout>{page}</SettingsLayout></Layout>;
